import { Globe, Phone, Hash, Mail } from 'lucide-react'
import type { Campaign } from '../lib/mock-data'
import { SeverityPill, StatusPill } from './pills'

const SOURCE_ICONS = {
  domain: Globe,
  entity: Hash,
  scall: Phone,
}

export function CampaignCard({
  campaign,
  onClick,
}: {
  campaign: Campaign
  onClick?: () => void
}) {
  const Icon = SOURCE_ICONS[campaign.sourceType] ?? Mail

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      className={`flex items-start gap-4 rounded-lg border border-border bg-card p-5 transition-shadow ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold text-foreground">{campaign.title}</span>
          <span className="shrink-0 rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {campaign.channel}
          </span>
          {campaign.sourceType === 'domain' && campaign.domain && (
            <span
              className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                campaign.domainActive
                  ? 'border-red-200 bg-red-50 text-red-800'
                  : 'border-border bg-secondary text-muted-foreground'
              }`}
            >
              {campaign.domainActive ? '● Activo' : '○ Inactivo'}
            </span>
          )}
          {campaign.sourceType === 'scall' && campaign.callType && (
            <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              {campaign.callType}
            </span>
          )}
        </div>

        {campaign.sender && (
          <div className="mt-0.5 truncate text-xs font-medium text-foreground">
            {campaign.sender}
          </div>
        )}

        {campaign.sourceType === 'entity' && campaign.entityReference && (
          <div className="mt-0.5 text-[11px] font-mono text-blue-700">
            {campaign.entityReference}
            {campaign.entityReferenceValue ? ` · ${campaign.entityReferenceValue}` : ''}
          </div>
        )}

        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {campaign.messageContent}
        </div>

        <div className="mt-1 text-[11px] text-muted-foreground">
          {campaign.type} · {campaign.reporter} · {campaign.date}
        </div>

        {campaign.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {campaign.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <SeverityPill severity={campaign.severity} />
        <StatusPill status={campaign.status} />
      </div>
    </div>
  )
}
